import { ReviewCanvasContext } from 'modules/match-video/domain/interfaces';
import { blockWidth, measureBlock, TextBlock } from './text-block';

export interface TeamPairLine {
  home: TextBlock;
  separator: TextBlock;
  away: TextBlock;
  /** Air on each side of the separator, px. */
  gap: number;
  width: number;
}

export interface TeamPairOptions {
  maxWidth: number;
  size: number;
  min: number;
  font: (px: number) => string;
  /** «×», «·». */
  separator: string;
  /** Air on each side of the separator: px, or `'space'` — one space of the font at that size (`«A · B»`). */
  gap: number | 'space';
}

/**
 * «Команда × Команда» in one line: both names shrink together down to the minimum; still too wide — each name is cut
 * with «…» to half of what is left, so neither team disappears.
 */
export function fitTeamPair(m: ReviewCanvasContext, home: string, away: string, options: TeamPairOptions): TeamPairLine {
  const at = (size: number, name: string, max = Number.POSITIVE_INFINITY) =>
    measureBlock(m, name, { maxWidth: max, size, min: size, font: options.font });
  const gapAt = (size: number) => {
    if (options.gap !== 'space') return options.gap;
    m.font = options.font(size);
    return m.measureText(' ').width;
  };
  const line = (h: TextBlock, separator: TextBlock, a: TextBlock, gap: number): TeamPairLine => ({
    home: h,
    separator,
    away: a,
    gap,
    width: blockWidth(h) + blockWidth(separator) + blockWidth(a) + 2 * gap,
  });

  for (let size = options.size; size >= options.min; size -= 1) {
    const result = line(at(size, home), at(size, options.separator), at(size, away), gapAt(size));
    if (result.width <= options.maxWidth) return result;
  }
  const separator = at(options.min, options.separator);
  const gap = gapAt(options.min);
  const half = Math.max(0, (options.maxWidth - blockWidth(separator) - 2 * gap) / 2);
  return line(at(options.min, home, half), separator, at(options.min, away, half), gap);
}
